"use client";

import { useAppSelector } from "@v2/lib/redux/hooks";
import { Box } from "@mui/material";
import { ReactNode, useEffect, useRef } from "react";

const BOTTOM_GAP = 16;

export default function BlogSidebarSticky({ children }: { children: ReactNode }) {
    const headerIsShow = useAppSelector((s) => s.general.headerIsShow);
    const ref = useRef<HTMLDivElement>(null);
    const lastScrollY = useRef(0);
    const currentTop = useRef<number | null>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const topGap = headerIsShow ? 124 : 52;

        if (currentTop.current === null) {
            currentTop.current = topGap;
        }
        el.style.top = `${currentTop.current}px`;
        lastScrollY.current = window.scrollY;

        const onScroll = () => {
            const sidebarHeight = el.offsetHeight;
            const viewportHeight = window.innerHeight;
            const parentHeight = el.parentElement?.offsetHeight ?? Infinity;
            const maxTop = topGap;
            const minTop = viewportHeight - sidebarHeight - BOTTOM_GAP;
            const scrollDelta = window.scrollY - lastScrollY.current;

            // sidebar fits in viewport OR sidebar is taller than parent column → fix at top
            if (sidebarHeight <= viewportHeight - topGap - BOTTOM_GAP || sidebarHeight >= parentHeight - 20) {
                currentTop.current = topGap;
            } else {
                currentTop.current = Math.min(
                    maxTop,
                    Math.max(minTop, (currentTop.current ?? topGap) - scrollDelta)
                );
            }

            el.style.top = `${currentTop.current}px`;
            lastScrollY.current = window.scrollY;
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [headerIsShow]);

    return (
        <Box ref={ref} sx={{ position: "sticky", top: headerIsShow ? 124 : 52, alignSelf: "flex-start", transition: "top 0.5s cubic-bezier(0.22,0.61,0.36,1)" }}>
            {children}
        </Box>
    );
}
